import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import extractText from 'flarum/common/utils/extractText';
const TAG_ENTRY_RE = /^tag\d+$/;
function isTagEntry(key) {
  return key === 'separator' || key === 'moreTags' || TAG_ENTRY_RE.test(key);
}
let labelsSynced = false;

/* =========================================================
   Schnellstartleiste erzeugen
   ========================================================= */
function ensureQuickbar() {
  let quickbar = document.querySelector('.qs-bar');
  if (quickbar) return quickbar;
  const header = document.querySelector('#header.App-header');
  if (!header) return null;
  quickbar = document.createElement('div');
  quickbar.className = 'qs-bar';
  header.insertAdjacentElement('afterend', quickbar);
  return quickbar;
}

/* =========================================================
   Schnellstartleiste rendern
   ========================================================= */
function renderQuickbar(menuItems) {
  const quickbar = ensureQuickbar();
  if (!quickbar || !Array.isArray(menuItems)) return;
  quickbar.innerHTML = '';
  menuItems.forEach(item => {
    if (!item || !item.label || !item.href) return;
    const link = document.createElement('a');
    link.className = 'qs-link';
    link.href = item.href;
    link.textContent = item.label;
    link.dataset.menuKey = item.key || '';
    try {
      const url = new URL(item.href, document.baseURI);
      if (url.origin === window.location.origin) {
        link.classList.add('UrlLink--internal');
      }
    } catch (e) {}
    if (item.icon) {
      link.dataset.icon = item.icon;
    }
    if (item.highlighted) {
      link.classList.add('qs-link--highlighted');
    }
    quickbar.appendChild(link);
  });
}

/* =========================================================
   Gespeicherte Navigation global laden
   Funktioniert auch auf Seiten ohne IndexSidebar
   ========================================================= */
function getStoredMenuItems() {
  const keys = app.forum.attribute('menuControlNavKeys') || [];
  const labels = app.forum.attribute('menuControlNavLabels') || {};
  const urls = app.forum.attribute('menuControlNavUrls') || {};
  const icons = app.forum.attribute('menuControlIcons') || {};
  return keys.filter(key => !isTagEntry(key)).map(key => ({
    key,
    label: labels[key] || key,
    href: urls[key] || '',
    icon: icons[key] || '',
    priority: 0
  })).filter(item => item.label && item.href);
}

/* =========================================================
   Menu-Control-Einstellungen nur auf Quickbar anwenden
   ========================================================= */
function buildHeaderPackItems(originalItems) {
  let menuItems = [...originalItems];
  const customLinks = app.forum.attribute('menuControlCustomLinks') || [];
  const customIcons = app.forum.attribute('menuControlCustomIcons') || {};
  const highlighted = app.forum.attribute('menuControlHighlighted') || [];
  const removedKeys = app.forum.attribute('menuControlRemovedKeys') || [];

  // ---------------------------------------------------------
  // Eigene Links NUR für Schnellstartleiste
  // ---------------------------------------------------------
  customLinks.forEach((link, index) => {
    if (!link || !link.url || !link.label) return;
    menuItems.push({
      key: `custom-link-${index}`,
      label: link.label,
      href: link.url,
      icon: link.icon || 'fas fa-link',
      priority: 50
    });
  });

  // ---------------------------------------------------------
  // Benutzerdefinierte Icons NUR für Schnellstartleiste
  // ---------------------------------------------------------
  menuItems = menuItems.map(item => ({
    ...item,
    icon: customIcons[item.key] || item.icon || '',
    highlighted: highlighted.includes(item.key)
  }));

  // ---------------------------------------------------------
  // Ausgeblendete Links NUR aus Schnellstartleiste entfernen
  // ---------------------------------------------------------
  menuItems = menuItems.filter(item => !removedKeys.includes(item.key));

  // ---------------------------------------------------------
  // Gespeicherte Reihenfolge NUR für Schnellstartleiste
  // ---------------------------------------------------------
  const rawOrder = app.forum.attribute('menuControlOrder');
  let menuOrder = [];
  if (rawOrder) {
    try {
      const parsed = JSON.parse(rawOrder);
      if (Array.isArray(parsed)) {
        menuOrder = parsed.filter(key => !isTagEntry(key));
      }
    } catch (e) {}
  }
  if (menuOrder.length > 0) {
    const positions = new Map(menuOrder.map((key, index) => [key, index]));
    menuItems.sort((a, b) => {
      const aPos = positions.has(a.key) ? positions.get(a.key) : Number.MAX_SAFE_INTEGER;
      const bPos = positions.has(b.key) ? positions.get(b.key) : Number.MAX_SAFE_INTEGER;
      if (aPos !== bPos) {
        return aPos - bPos;
      }
      return b.priority - a.priority;
    });
  } else {
    menuItems.sort((a, b) => b.priority - a.priority);
  }

  // ---------------------------------------------------------
  // Umkehren betrifft ebenfalls NUR Schnellstartleiste
  // ---------------------------------------------------------
  if (app.forum.attribute('menuControlFlip')) {
    menuItems.reverse();
  }
  return menuItems;
}
app.initializers.add('resofire-menu-control', () => {
  app.beforeMount(() => {
    ensureQuickbar();
    const storedItems = getStoredMenuItems();
    const headerPackItems = buildHeaderPackItems(storedItems);
    window.headerPackMenuItems = headerPackItems;
    renderQuickbar(headerPackItems);
  });

  /* =========================================================
     Originale Flarum Item Nav AUSLESEN
     Nicht verändern!
     ========================================================= */
  extend(IndexSidebar.prototype, 'navItems', function (items) {
    const origToArray = items.toArray.bind(items);
    items.toArray = function (keepPrimitives) {
      const originalMenuItems = [];
      const knownKeys = Object.keys(items.toObject()).filter(key => !isTagEntry(key));
      knownKeys.forEach(key => {
        try {
          const vnode = items.get(key);
          const label = extractText(vnode);
          const href = vnode?.attrs?.href;
          const icon = vnode?.attrs?.icon || '';
          const priority = items.getPriority(key);
          if (!label || !href) return;
          originalMenuItems.push({
            key,
            label: label.trim(),
            href,
            icon,
            priority
          });
        } catch (e) {}
      });

      /* =====================================================
         Namen + Icons für Adminbereich synchronisieren
         ===================================================== */
      if (!labelsSynced && app.session.user && app.session.user.isAdmin()) {
        labelsSynced = true;
        const labels = {};
        const icons = {};
        const urls = {};
        originalMenuItems.forEach(item => {
          labels[item.key] = item.label;
          urls[item.key] = item.href;
          if (item.icon) {
            icons[item.key] = item.icon;
          }
        });
        app.request({
          method: 'POST',
          url: app.forum.attribute('apiUrl') + '/settings',
          body: {
            'resofire-menu-control.known-keys': JSON.stringify(Object.keys(labels)),
            'resofire-menu-control.labels': JSON.stringify(labels),
            'resofire-menu-control.icons': JSON.stringify(icons),
            'resofire-menu-control.urls': JSON.stringify(urls)
          }
        }).catch(() => {});
      }
      /* =====================================================
         Eigene Quickbar-Liste erstellen
         ===================================================== */
      const headerPackItems = buildHeaderPackItems(originalMenuItems);
      window.headerPackMenuItems = headerPackItems;
      renderQuickbar(headerPackItems);
      /* =====================================================
         ORIGINAL ItemList unverändert an Flarum zurückgeben
         ===================================================== */
      items.toArray = origToArray;
      return origToArray(keepPrimitives);
    };
  });
});